import React, { useState, useCallback } from 'react';
import { Icon } from '@hiro-ui/bootstrap';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';

import { SidebarNode, SidebarValue, SidebarSection } from './types';
import { StyledListItem } from './styles';

interface SidebarItemProps {
  value: SidebarValue;
  title?: string;
  level?: number;
}

const isNode = (value: SidebarValue): value is SidebarNode => !!value.id;

const getPathAt = (pathname: string, i: number) => {
  const p = pathname.split('/').filter(Boolean);

  if (!p || !i) {
    return;
  }

  return p[i];
};

const sortSidebarValue = (value: SidebarSection) => (a: string, b: string) => {
  const aV = value[a];
  const bV = value[b];

  if (isNode(aV) && isNode(bV)) {
    const indexA = aV.pageAttributes.sidebar_index;
    const indexB = bV.pageAttributes.sidebar_index;

    if (indexA !== null || indexB !== null) {
      return (indexB ?? 0) - (indexA ?? 0);
    }
  }

  if (isNode(bV)) {
    if (bV.fields.location.length === 1) {
      return 1;
    }
  }

  if (isNode(aV)) {
    if (aV.fields.location.length === 1) {
      return -1;
    }

    if (isNode(bV)) {
      return (
        aV.pageAttributes.sidebar_title || aV.document.main
      ).localeCompare(bV.pageAttributes.sidebar_title || bV.document.main);
    }

    return 1;
  }

  return -1;
};

const regexHasVersion = /^\/\d/;

export const SidebarItem: React.FC<SidebarItemProps> = ({
  title,
  value,
  level = 0,
}) => {
  const { pathname } = useLocation();

  const cleanPathname = regexHasVersion.test(pathname)
    ? '/' + pathname.split('/').filter(Boolean).slice(1).join('/') + '/'
    : pathname;

  const childIsOpen = getPathAt(cleanPathname, level) === title;
  const [open, setOpen] = useState(childIsOpen);

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);

  if (isNode(value)) {
    const isActive = value.fields.slug === cleanPathname;

    return (
      <StyledListItem
        onClick={() => navigate(value.fields.slug)}
        active={isActive}
      >
        {value.pageAttributes.sidebar_title || value.document.main}
      </StyledListItem>
    );
  }

  const cleanTitle =
    title &&
    title
      .split('_')
      .map((t) => t[0].toUpperCase().concat(t.slice(1)))
      .join(' ');

  const values = Object.keys(value).sort(sortSidebarValue(value));

  return (
    <>
      {title && (
        <StyledListItem onClick={toggleOpen} childIsOpen={childIsOpen}>
          {cleanTitle} <Icon name={open ? 'chevron-down' : 'chevron-right'} />
        </StyledListItem>
      )}
      {(open || !title) && (
        <ul>
          {values.map((k) => (
            <SidebarItem
              key={(value[k].id as any) || k}
              title={k}
              value={value[k]}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </>
  );
};
